import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Users, Mail, Rocket } from "lucide-react";
import api from "../api";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { AppCard, AppIcon, MysteryCard } from "../components/AppCard";
import { BooksSection } from "../components/Books";
import { TrailerSection } from "../components/Trailer";
import { NotifyForm } from "../components/NotifyForm";
import { MonkeyMark } from "../components/Logo";

const chapters = [
  {
    num: "01",
    title: "Curiosity First",
    text: "Every game starts with a question worth answering. Animals, space, history — learning follows wonder.",
    color: "#FFC72C",
  },
  {
    num: "02",
    title: "Safe by Design",
    text: "No ads, no sneaky purchases, no noise. Just calm, colorful play that everyone can trust completely.",
    color: "#34D399",
  },
  {
    num: "03",
    title: "Play as Pedagogy",
    text: "Stars, badges and quests turn practice into adventure. We learn more when we're having too much fun to notice.",
    color: "#38BDF8",
  },
];

export const ComingSoonBadge = ({ size = "md" }) => (
  <span
    data-testid="coming-soon-badge"
    className={`inline-flex items-center gap-2 self-start font-black uppercase tracking-widest rounded-full bg-grape/15 text-ink border-2 border-dashed border-ink/40 ${
      size === "lg" ? "text-sm px-5 py-2.5" : "text-xs px-3.5 py-2"
    }`}
  >
    <Rocket size={size === "lg" ? 16 : 13} className="text-coral" />
    Coming soon to app stores
  </span>
);

export default function Home() {
  const [apps, setApps] = useState([]);
  useEffect(() => {
    api.get("/apps").then((r) => setApps(r.data)).catch(() => {});
  }, []);

  const featured = apps.find((a) => a.featured) || apps[0];
  const { scrollY } = useScroll();
  const yBlob = useTransform(scrollY, [0, 600], [0, 120]);
  const yBlob2 = useTransform(scrollY, [0, 600], [0, -80]);

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden px-4 sm:px-8 pt-14 sm:pt-20 pb-20">
        <motion.div
          style={{ y: yBlob }}
          className="absolute -top-10 -left-24 w-72 h-72 rounded-full bg-sun/40 blur-3xl pointer-events-none"
        />
        <motion.div
          style={{ y: yBlob2 }}
          className="absolute top-40 -right-24 w-80 h-80 rounded-full bg-skykit/30 blur-3xl pointer-events-none"
        />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-ink shadow-brutal-sm text-xs font-extrabold uppercase tracking-[0.2em]"
            >
              <Sparkles size={14} className="text-coral" /> KITOE entertainment · Learning games studio
            </motion.div>

            <h1
              data-testid="hero-title"
              className="mt-6 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            >
              <MaskedLines
                lines={[
                  "Playful learning",
                  "for curious",
                  <span key="l3" className="text-coral">
                    minds
                  </span>,
                ]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed max-w-xl"
            >
              We craft colorful quiz and learning games for curious players — kids,
              families, and anyone who loves a good challenge. Explore animals, space,
              history and science, earn stars, and have a blast while learning.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/apps"
                data-testid="hero-explore-games-button"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-coral text-white font-display font-extrabold text-lg border-2 border-ink shadow-brutal hover:-translate-x-1 hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
              >
                Explore our games <ArrowRight size={20} />
              </Link>
              <ComingSoonBadge size="lg" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
            >
              {[
                { icon: Users, label: "Fun for everyone" },
                { icon: ShieldCheck, label: "100% Ad-Free" },
                { icon: Sparkles, label: "Kid-Safe Fun" },
              ].map((s) => (
                <span key={s.label} className="inline-flex items-center gap-2 font-bold text-sm text-ink/70">
                  <s.icon size={18} className="text-coral" /> {s.label}
                </span>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="w-64 h-64 sm:w-80 sm:h-80 rounded-[3rem] bg-sun border-2 border-ink shadow-brutal-lg flex items-center justify-center rotate-3">
                <div className="animate-floaty">
                  <MonkeyMark size={200} className="hidden sm:block" />
                  <MonkeyMark size={150} className="sm:hidden" />
                </div>
              </div>
              <motion.div
                animate={{ y: [0, -10, 0], rotate: [0, 6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-5 -right-5 bg-white border-2 border-ink rounded-2xl shadow-brutal-sm px-4 py-2 font-display font-extrabold text-sm"
              >
                ⭐ Earn stars!
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                className="absolute -bottom-4 -left-6 bg-mint border-2 border-ink rounded-2xl shadow-brutal-sm px-4 py-2 font-display font-extrabold text-sm"
              >
                🏅 Win badges!
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED APP */}
      {featured && (
        <section className="px-4 sm:px-8 py-20 sm:py-28">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
                Featured app
              </span>
              <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Our latest adventure
              </h2>
            </Reveal>

            <Reveal delay={0.15} className="mt-10">
              <div
                data-testid="featured-app-card"
                className="grid lg:grid-cols-12 rounded-[2.5rem] border-2 border-ink bg-white shadow-brutal-lg overflow-hidden"
              >
                <div className="lg:col-span-7 p-8 sm:p-12 flex flex-col gap-6">
                  <div className="flex items-center gap-4">
                    <AppIcon icon={featured.icon} color={featured.color} box="w-20 h-20" text="text-5xl" />
                    <div>
                      <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                        {featured.name}
                      </h3>
                      <span className="inline-block mt-1 text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full bg-sun border-2 border-ink">
                        {featured.age_range}
                      </span>
                    </div>
                  </div>
                  <p className="text-base sm:text-lg text-ink/70 leading-relaxed">
                    {featured.description}
                  </p>
                  {featured.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {featured.tags.map((t) => (
                        <span
                          key={t}
                          className="text-sm font-bold px-3.5 py-1.5 rounded-full bg-cream border-2 border-ink/15"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <ComingSoonBadge size="lg" />
                </div>
                <div
                  className="lg:col-span-5 border-t-2 lg:border-t-0 lg:border-l-2 border-ink flex items-center justify-center p-10 min-h-[280px]"
                  style={{ backgroundColor: featured.color }}
                >
                  <motion.div
                    animate={{ y: [0, -12, 0] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                    className="text-[7rem] sm:text-[9rem] leading-none drop-shadow-sm select-none"
                  >
                    {featured.icon?.startsWith("http") ? (
                      <img src={featured.icon} alt={featured.name} className="w-48 h-48 object-cover rounded-3xl border-2 border-ink" />
                    ) : (
                      featured.icon
                    )}
                  </motion.div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* MORE GAMES */}
      <section className="px-4 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
                The collection
              </span>
              <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Fresh from the studio
              </h2>
            </div>
            <Link
              to="/apps"
              data-testid="home-view-all-games-link"
              className="inline-flex items-center gap-2 font-bold border-b-2 border-coral text-ink hover:text-coral transition-colors"
            >
              View all games <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.slice(0, 3).map((app, i) => (
              <Reveal key={app.id} delay={i * 0.1}>
                <AppCard app={app} />
              </Reveal>
            ))}
            <Reveal delay={Math.min(apps.length, 3) * 0.1}>
              <MysteryCard />
            </Reveal>
          </div>
        </div>
      </section>

      <BooksSection />

      <TrailerSection />

      <Marquee dark />

      {/* MANIFESTO TEASER */}
      <section className="px-4 sm:px-8 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
              Our manifesto
            </span>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">
              Why we make games
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12}>
                <Link to="/about" data-testid={`manifesto-chapter-${c.num}`} className="block h-full">
                  <motion.div
                    whileHover={{ x: -4, y: -4 }}
                    className="h-full rounded-3xl border-2 border-ink bg-white shadow-brutal-sm hover:shadow-brutal p-7 flex flex-col gap-4"
                  >
                    <span
                      className="w-12 h-12 rounded-2xl border-2 border-ink flex items-center justify-center font-display font-black text-lg"
                      style={{ backgroundColor: c.color }}
                    >
                      {c.num}
                    </span>
                    <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{c.title}</h3>
                    <p className="text-sm sm:text-base text-ink/70 leading-relaxed">{c.text}</p>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LAUNCH NOTIFY */}
      <section className="px-4 sm:px-8 pb-24">
        <Reveal>
          <div className="max-w-4xl mx-auto rounded-[2.5rem] border-2 border-ink bg-skykit/25 shadow-brutal-lg p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-12 -left-12 w-44 h-44 rounded-full bg-sun/40 blur-2xl" />
            <div className="relative">
              <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
                Launch alerts
              </span>
              <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Be first to play
              </h2>
              <p className="mt-4 text-ink/70 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                Get an email the moment QuizKids Adventure hits the App Store and Google
                Play — and whenever a new game joins the collection. No spam, ever.
              </p>
              <NotifyForm />
            </div>
          </div>
        </Reveal>
      </section>

      {/* CONTACT CTA */}
      <section className="px-4 sm:px-8 pb-24">
        <Reveal>
          <div className="max-w-7xl mx-auto rounded-[2.5rem] border-2 border-ink bg-coral shadow-brutal-lg p-10 sm:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-sun/50 blur-2xl" />
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
              Have an idea for a game?
            </h2>
            <p className="mt-4 text-white/85 text-base sm:text-lg max-w-xl mx-auto">
              We love hearing from players, parents and teachers alike. Drop us a line —
              we read every single email.
            </p>
            <a
              href="mailto:kitoe.entertainment@gmail.com"
              data-testid="home-contact-cta-button"
              className="mt-8 inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-white text-ink font-display font-extrabold text-lg border-2 border-ink shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
            >
              <Mail size={20} /> Say hello
            </a>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
